import { HTMLAttributes } from 'react';

import { service } from '@maia/attachments';
import { downloadEncodedDocument } from '@maia/utils';
import { useMutation } from '@tanstack/react-query';
import { twMerge } from 'tailwind-merge';
import {
  cardStyles,
  MarkdownRenderer,
  Typography,
  UploadedFile,
} from '../../../ui';

type Props = {
  user: string;
  title: string;
  date: string;
  isUneven?: boolean;
  attachments?:
    | Array<{
        guid: string;
        filename: string;
        mimetype: string;
      }>
    | [];
} & HTMLAttributes<HTMLDivElement>;

export function NoteCard(props: Props) {
  const {
    user,
    title,
    date,
    isUneven,
    content,
    className,
    children,
    attachments,
    ...rest
  } = props;

  const { mutateAsync } = useMutation({
    mutationFn: (guid: string) => service.find(guid),
  });

  async function handleDownload(guid: string) {
    const documentData = await mutateAsync(guid);

    if (documentData) {
      return downloadEncodedDocument(documentData);
    }
  }

  return (
    <div
      className={twMerge(
        cardStyles({ variant: isUneven ? 'flat' : 'outlined' }),
        isUneven && 'border-grayscale100 border',
        'p-24',
        className,
      )}
      {...rest}
    >
      <div className="flex flex-row items-center gap-x-4">
        <Typography variant="body-s" weight="semibold" className="text-info300">
          {user}
        </Typography>
        <Typography
          variant="body-s"
          weight="semibold"
          className="text-grayscale600"
        >
          {title}
        </Typography>
      </div>

      <Typography
        variant="body-xxs"
        weight="regular"
        className="text-grayscale300 mt-8"
      >
        {date}
      </Typography>
      <MarkdownRenderer markdown={children as string} />
      {!!attachments && (
        <ul className="flex flex-wrap gap-8 mt-12">
          {attachments?.map((attachment) => (
            <li key={attachment.filename} className="w-fit">
              <UploadedFile
                mimetype={attachment.mimetype}
                name={attachment.filename}
                action="download"
                onDownload={() => handleDownload(attachment.guid)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
